import "@mdi/font/css/materialdesignicons.css";
import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import { createRouter } from "./router";
import vuetify from "./vuetify"; // 引入 Vuetify 設定

async function initializeApp() {
  const pinia = createPinia();
  const app = createApp(App);

  const isAuth0Enabled = process.env.VUE_APP_AUTH0_ENABLED !== 'false';
  let routerInstance; // Will hold the resolved router

  try {
    // Create router instance (could be async)
    // createRouter() itself determines if it's async based on isAuth0Enabled
    const routerResult = createRouter(); // This call happens outside Auth0 specific logic for now
                                       // but its resolution might depend on Auth0 SDK if createAuthGuard is used.

    if (isAuth0Enabled) {
      console.log("Auth0 is enabled. Initializing Auth0...");
      // Dynamically import Auth0 related modules only when enabled
      // It's important that auth_config.json is loaded correctly
      const [authConfigModule, auth0VueModule] = await Promise.all([
        import('../auth_config.json').catch(err => {
          console.error("Failed to load auth_config.json. Ensure the file exists and is valid.", err);
          throw new Error("auth_config.json loading failed"); // Re-throw to be caught by outer try-catch
        }),
        import('@auth0/auth0-vue').catch(err => {
          console.error("Failed to load @auth0/auth0-vue module. Ensure it's installed.", err);
          throw new Error("@auth0/auth0-vue loading failed"); // Re-throw
        })
      ]);

      const authConfig = authConfigModule.default;
      const { createAuth0 } = auth0VueModule;

      if (!authConfig || !authConfig.domain || !authConfig.clientId) {
        console.error("auth_config.json is missing domain or clientId.");
        throw new Error("Invalid Auth0 configuration in auth_config.json");
      }

      const auth0Client = createAuth0({
        domain: authConfig.domain,
        clientId: authConfig.clientId,
        authorizationParams: {
          redirect_uri: window.location.origin,
        },
        // Adding a timeout and cacheLocation for robustness, though not strictly necessary for this fix
        // cacheLocation: 'localstorage', 
        // timeoutInSeconds: 60,
      });
      app.use(auth0Client);
      app.provide('auth0Client', auth0Client); // 將 Auth0 客戶端透過 provide 提供給應用
      console.log("Auth0 initialized and used.");
    } else {
      console.log("Auth0 is disabled. Skipping Auth0 initialization.");
    }

    // Now that Auth0 (if enabled) is initialized, resolve the router
    // If createRouter() returns a Promise (i.e., when Auth0 is enabled and createAuthGuard is imported),
    // we await its resolution. Otherwise, it's used directly.
    if (typeof routerResult.then === 'function') { // It's a Promise
      console.log("Router creation is async, awaiting...");
      routerInstance = await routerResult;
    } else { // It's a direct instance
      console.log("Router creation is sync.");
      routerInstance = routerResult;
    }
    
    if (!routerInstance) {
        throw new Error("Router instance could not be created.");
    }

    console.log("Router instance created. Using router, pinia, and vuetify.");
    // Mount the app after Auth0 (if enabled) and router are configured
    app.use(routerInstance);
    app.use(pinia);
    app.use(vuetify);

    console.log("Mounting app to #app...");
    app.mount("#app");
    console.log("App mounted successfully.");

  } catch (error) {
    console.error("Failed to initialize and mount the application:", error);
    // Display a user-friendly error message in the DOM as a fallback
    const appElement = document.getElementById('app');
    if (appElement) {
      appElement.innerHTML = '<h2>Application Error</h2><p>Sorry, the application could not be started. Please check the console for more details or try again later.</p>';
    }
  }
}

initializeApp();
